import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
// Avoid static import to prevent circular dependency (api -> store -> contactsSlice -> api)
let contactService;
const getService = async () => {
  if (!contactService) {
    const mod = await import('../../services/api');
    contactService = mod.contactService;
  }
  return contactService;
};

const initialState = {
  list: [],
  pagination: {},
  loading: false,
  error: null,
};

// Fetch contacts (GET /users/contacts/)
export const fetchContacts = createAsyncThunk(
  'contacts/fetchContacts',
  async (params = {}, { rejectWithValue }) => {
    const service = await getService();
    const res = await service.getContacts(params);
    if (res.success) return { data: res.data, pagination: res.pagination };
    return rejectWithValue(res.error || 'Failed to fetch contacts');
  }
);

// Add new contact (POST)
export const addContact = createAsyncThunk(
  'contacts/addContact',
  async (payload, { rejectWithValue }) => {
    const service = await getService();
    // Ensure payload has the correct field names for the API
    const apiPayload = {
      name: payload.name,
      job_title: payload.job_title || payload.designation || '',
      phone_number: payload.phone_number || '',
      email: payload.email || '',
      address: payload.address || '',
      city: payload.city || '',
      country: payload.country || '',
      zipcode: payload.zipcode || ''
    };
    const res = await service.createContact(apiPayload);
    if (res.success) return res.data;
    return rejectWithValue(res.error || 'Failed to create contact');
  }
);

// Delete contact (DELETE)
export const deleteContact = createAsyncThunk(
  'contacts/deleteContact',
  async (id, { rejectWithValue }) => {
    const service = await getService();
    const res = await service.deleteContact(id);
    if (res.success) return id;
    return rejectWithValue(res.error || 'Failed to delete contact');
  }
);

// Update contact (PUT/PATCH)
export const updateContact = createAsyncThunk(
  'contacts/updateContact',
  async ({ id, payload }, { rejectWithValue }) => {
    const service = await getService();
    // Ensure payload has the correct field names for the API
    const apiPayload = {
      name: payload.name,
      job_title: payload.job_title || payload.designation || '',
      phone_number: payload.phone_number || '',
      email: payload.email || '',
      address: payload.address || '',
      city: payload.city || '',
      country: payload.country || '',
      zipcode: payload.zipcode || ''
    };
    const res = await service.updateContact(id, apiPayload, true);
    if (res.success) return res.data;
    return rejectWithValue(res.error || 'Failed to update contact');
  }
);

const contactsSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    resetContactsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch contacts';
      })
      // Add
      .addCase(addContact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addContact.fulfilled, (state, action) => {
        state.loading = false;
        // Prepend new contact for immediate visibility
        state.list = [action.payload, ...state.list];
      })
      .addCase(addContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to add contact';
      })
      // Delete
      .addCase(deleteContact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteContact.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter((c) => c.id !== action.payload);
      })
      .addCase(deleteContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete contact';
      })
      // Update
      .addCase(updateContact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateContact.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.map((c) => (c.id === action.payload.id ? action.payload : c));
      })
      .addCase(updateContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update contact';
      });
  },
});

export const { resetContactsState } = contactsSlice.actions;

export default contactsSlice.reducer; 